import { useEffect, useState } from "react";
import { ClipboardList, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { normContexte } from "@/lib/checklist";
import { countReponses, fetchQuestionnaire, saveQuestionnaire, extractEtatCivil, extractIdentiteDirigeant, extractComptesBancaires, extractRepartitionCapital, extractAffiliations } from "@/lib/questionnaire";
import { uploadDocument, extraireDocumentAsync, fetchDocuments, deleteDocument } from "@/lib/dataroom";
import { fetchAnalyse, fetchEtablissements } from "@/lib/entreprises";
import { supabase } from "@/lib/supabase";
import QuestionnaireForm from "@/components/questionnaire/QuestionnaireForm";

export default function QuestionnaireDirigeant({ dossierId, contexteAudit, onSaved }) {
    const [q, setQ] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [societes, setSocietes] = useState([]);
    const [payslips, setPayslips] = useState([]);
    const [identiteDocs, setIdentiteDocs] = useState([]);
    const [releveDocs, setReleveDocs] = useState([]);
    const [statutsDocs, setStatutsDocs] = useState([]);
    const [etablissements, setEtablissements] = useState([]);
    const [siren, setSiren] = useState("");

    // LOT 59 — Sociétés du dirigeant (question Holding) : lues depuis l'analyse Pappers (mise en cache 30 j), best-effort.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await supabase.from("dossiers").select("entreprises(siren)").eq("id", dossierId).single();
                const siren = data?.entreprises?.siren;
                if (!siren) return;
                if (!cancelled) setSiren(String(siren));
                const a = await fetchAnalyse(siren, dossierId);
                const list = (a?.groupe?.societes || []).filter((s) => s.siren && !s.auditee).map((s) => ({ siren: s.siren, nom: s.nom_entreprise || s.nom || s.siren }));
                if (!cancelled) setSocietes(list);
                fetchEtablissements(siren).then((r) => { if (!cancelled) setEtablissements(r?.etablissements || []); }).catch(() => {});
            } catch (e) { console.debug("Analyse Pappers (holding) indisponible :", e?.message || e); /* Pappers non lancé / indisponible : la saisie « Autre → SIREN » reste possible */ }
        })();
        return () => { cancelled = true; };
    }, [dossierId]);

    async function loadDocs() {
        try {
            const ds = await fetchDocuments(dossierId);
            setPayslips((ds || []).filter((d) => d.type_document === "fiches_paie" && !d.est_holding));
            setIdentiteDocs((ds || []).filter((d) => d.type_document === "identite_gerant" && !d.est_holding));
            setStatutsDocs((ds || []).filter((d) => d.type_document === "statuts" && !d.est_holding));
            setReleveDocs((ds || []).filter((d) => (d.type_document === "releve_bancaire" || d.type_document === "rib") && !d.est_holding).map((d) => ({ ...d, _kind: d.type_document === "rib" || /rib/i.test(`${d.type_personnalise || ""} ${d.nom_fichier || ""}`) ? "rib" : "releve" })));
        } catch (e) { console.debug("Chargement documents :", e?.message || e); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadDocs(); }, [dossierId]);

    async function onDeleteDoc(doc) {
        try { await deleteDocument(doc); toast.success("Document supprimé"); loadDocs(); }
        catch (e) { toast.error("Suppression impossible", { description: e.message }); }
    }

    async function uploadPayslips(files) {
        for (const f of files) {
            try {
                const doc = await uploadDocument(dossierId, f, "fiches_paie");
                extraireDocumentAsync(doc.id).catch(() => {});
            } catch (e) { toast.error("Envoi impossible", { description: `${f.name} — ${e.message}` }); }
        }
        toast.success(`${files.length} fiche(s) de paie déposée(s)`, { description: "Extraction en cours — puis cliquez sur « Pré-remplir l'état civil »." });
        loadDocs();
    }

    async function uploadIdentite(files) {
        for (const f of files) {
            try { const doc = await uploadDocument(dossierId, f, "identite_gerant"); extraireDocumentAsync(doc.id).catch(() => {}); }
            catch (e) { toast.error("Envoi impossible", { description: `${f.name} — ${e.message}` }); }
        }
        toast.success(`${files.length} pièce(s) déposée(s)`, { description: "Extraction en cours — puis « Pré-remplir via l'IA »." });
        loadDocs();
    }

    async function uploadReleve(files, kind) {
        for (const f of files) {
            try { const doc = await uploadDocument(dossierId, f, kind === "rib" ? "rib" : "releve_bancaire", "", kind === "rib" ? "RIB" : "Relevé bancaire"); extraireDocumentAsync(doc.id).catch(() => {}); }
            catch (e) { toast.error("Envoi impossible", { description: `${f.name} — ${e.message}` }); }
        }
        toast.success(`${files.length} document(s) déposé(s)`, { description: "Extraction en cours — puis « Pré-remplir les banques »." });
        loadDocs();
    }

    async function prefillEtatCivil() {
        try {
            const r = await extractEtatCivil(dossierId);
            setDirty(true);
            toast.success(`${r.lignes.length} salarié(s) extrait(s)`);
            return r;
        } catch (e) { toast.error("Pré-remplissage impossible", { description: e.message }); return null; }
    }

    async function prefillIdentite() {
        try { const r = await extractIdentiteDirigeant(dossierId); setDirty(true); toast.success(`${r.lignes.length} dirigeant(s) extrait(s)`); return r; }
        catch (e) { toast.error("Extraction impossible", { description: e.message }); return null; }
    }

    async function prefillBanques() {
        try { const r = await extractComptesBancaires(dossierId); setDirty(true); toast.success(`${r.lignes.length} compte(s) extrait(s)`); return r; }
        catch (e) { toast.error("Extraction impossible", { description: e.message }); return null; }
    }

    async function uploadStatuts(files) {
        for (const f of files) {
            try { const doc = await uploadDocument(dossierId, f, "statuts"); extraireDocumentAsync(doc.id).catch(() => {}); }
            catch (e) { toast.error("Envoi impossible", { description: `${f.name} — ${e.message}` }); }
        }
        toast.success(`${files.length} document(s) déposé(s)`, { description: "Extraction en cours — puis « Pré-remplir la répartition »." });
        loadDocs();
    }

    async function prefillCapital() {
        try { const r = await extractRepartitionCapital(dossierId); setDirty(true); toast.success(`${r.lignes.length} associé(s) extrait(s)`); return r; }
        catch (e) { toast.error("Extraction impossible", { description: e.message }); return null; }
    }

    async function prefillAffiliations() {
        try { const r = await extractAffiliations(dossierId); setDirty(true); toast.success("Affiliations extraites des fiches de paie"); return r; }
        catch (e) { toast.error("Extraction impossible", { description: e.message }); return null; }
    }

    useEffect(() => {
        let cancelled = false;
        fetchQuestionnaire(dossierId)
            .then((data) => {
                if (cancelled) return;
                // LOT 35 — le Contexte de la mission (Admin) écrase et synchronise le « Contexte de votre demande » ; le client ne le re-saisit pas.
                const norm = contexteAudit ? normContexte(contexteAudit) : null;
                if (norm && data?.contexte_demande !== norm) {
                    data = { ...data, contexte_demande: norm };
                    saveQuestionnaire(dossierId, data).catch(() => {});
                }
                setQ(data || {});
            })
            .catch((e) => toast.error("Questionnaire", { description: /questionnaire_qualitatif/.test(e.message) ? "Colonne manquante — exécutez supabase_migration_5.sql dans Supabase." : e.message }))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [dossierId, contexteAudit]);

    async function save() {
        if (!q?.contexte_demande) return toast.error("Champ obligatoire", { description: "Sélectionnez le contexte de votre demande en haut du questionnaire." });
        setSaving(true);
        try {
            const saved = await saveQuestionnaire(dossierId, q);
            setQ(saved);
            setDirty(false);
            onSaved?.(saved);
            toast.success("Questionnaire enregistré");
        } catch (e) {
            toast.error("Enregistrement impossible", { description: e.message });
        } finally {
            setSaving(false);
        }
    }

    const { done, total } = countReponses(q);
    const contributors = Object.keys(q?._contributions || {});
    const updated = q?._meta?.updated_at ? new Date(q._meta.updated_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : null;

    return (
        <section data-testid="questionnaire-section" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center">
                        <ClipboardList className="h-4 w-4 text-primary" strokeWidth={1.75} />
                    </div>
                    <div>
                        <h2 className="font-display text-2xl text-foreground">Questionnaire dirigeant</h2>
                        <p className="text-xs text-muted-foreground">
                            Contexte qualitatif en 6 thèmes, injecté dans l&apos;analyse IA (test de l&apos;excuse). <span data-testid="questionnaire-progress">{done}/{total} réponses cabinet</span>
                            {contributors.length > 0 && <span data-testid="questionnaire-contributors"> · {contributors.length} contributeur{contributors.length > 1 ? "s" : ""} via le portail : {contributors.join(", ")}</span>}
                            {updated && ` · mis à jour le ${updated}`}
                        </p>
                    </div>
                </div>
                <Button data-testid="questionnaire-save-button" onClick={save} disabled={saving || loading || !dirty} className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground">
                    <Save className="h-4 w-4" strokeWidth={2} /> {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
            </div>

            {loading ? (
                <p className="text-sm text-muted-foreground">Chargement…</p>
            ) : (
                <>
                    <QuestionnaireForm value={q} onChange={(v) => { setQ(v); setDirty(true); }} contributions={q} placeholder="Décrivez ici…" lockContexte={!!contexteAudit} societesGroupe={societes} siren={siren} onUploadPayslip={uploadPayslips} onPrefillEtatCivil={prefillEtatCivil} showHistorique payslipDocs={payslips} onDeletePayslip={onDeleteDoc} onUploadIdentite={uploadIdentite} identiteDocs={identiteDocs} onDeleteIdentite={onDeleteDoc} onPrefillIdentite={prefillIdentite} etablissements={etablissements} onUploadReleve={uploadReleve} releveDocs={releveDocs} onDeleteReleve={onDeleteDoc} onPrefillBanques={prefillBanques} onUploadStatuts={uploadStatuts} statutsDocs={statutsDocs} onDeleteStatuts={onDeleteDoc} onPrefillCapital={prefillCapital} onPrefillAffiliations={prefillAffiliations} />
                    <div className="flex justify-end border-t border-slate-200 pt-4">
                        <Button data-testid="questionnaire-save-button-bottom" onClick={save} disabled={saving || !dirty} className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground">
                            <Save className="h-4 w-4" strokeWidth={2} /> {saving ? "Enregistrement…" : "Enregistrer"}
                        </Button>
                    </div>
                </>
            )}
        </section>
    );
}
